// Extra CMS slots for the 'notes_from_the_road' page — added to make previously-hardcoded
// public marketing copy editable. Concatenated into CMS_SLOTS via ./index.
// Each default MUST equal the exact copy the component rendered before wiring,
// so the public page is byte-identical until an admin edits it.
import type { SlotDef } from '../slots';

export const notesFromTheRoadExtra: SlotDef[] = [
  // ─── Index hero (eyebrow / title / lede) ──────────────────────────────
  {
    key: 'notes_from_the_road.hero_eyebrow',
    page: 'notes_from_the_road',
    kind: 'markdown',
    label: 'Hero eyebrow',
    default: 'Quarterly · written by Richie',
  },
  {
    key: 'notes_from_the_road.hero_title',
    page: 'notes_from_the_road',
    kind: 'markdown',
    label: 'Hero title',
    default: 'Notes from the road.',
  },
  {
    key: 'notes_from_the_road.hero_lede',
    page: 'notes_from_the_road',
    kind: 'markdown',
    label: 'Hero lede',
    default:
      "Letters from the founder about what's working, what isn't, and what we're building next. Slow, considered, public on purpose.",
  },

  // ─── The archive section (two states: no letters yet vs. published) ────
  {
    key: 'notes_from_the_road.archive_eyebrow',
    page: 'notes_from_the_road',
    kind: 'markdown',
    label: 'Archive eyebrow',
    default: 'The archive',
  },
  {
    key: 'notes_from_the_road.archive_title_empty',
    page: 'notes_from_the_road',
    kind: 'markdown',
    label: 'Archive title — no letters yet',
    helpText: 'Section heading shown while no letters have been published.',
    default: 'The first letter is on its way.',
  },
  {
    key: 'notes_from_the_road.archive_title_active',
    page: 'notes_from_the_road',
    kind: 'markdown',
    label: 'Archive title — letters published',
    helpText: 'Section heading shown once one or more letters exist.',
    default: 'Every letter, in order.',
  },
  {
    key: 'notes_from_the_road.archive_lede_empty',
    page: 'notes_from_the_road',
    kind: 'markdown',
    label: 'Archive lede — no letters yet',
    helpText: 'Section lede shown while no letters have been published.',
    default:
      "We post a new note at the end of every quarter: what we shipped, what we got wrong, what's next. The first one lands soon.",
  },
  {
    key: 'notes_from_the_road.archive_lede_active',
    page: 'notes_from_the_road',
    kind: 'markdown',
    label: 'Archive lede — letters published',
    helpText: 'Section lede shown once one or more letters exist.',
    default: 'Each one lands at the end of the quarter. Skim the excerpt; open the ones that catch you.',
  },
  {
    key: 'notes_from_the_road.archive_empty_body',
    page: 'notes_from_the_road',
    kind: 'markdown',
    label: 'Empty-state message',
    helpText: 'Shown in place of the letter list while no letters have been published.',
    default:
      'We post a new note at the end of every quarter: what we shipped, what we got wrong, what is next.',
  },

  // ─── Archive empty-state early-access CTA ────────────────────────────
  {
    key: 'notes_from_the_road.subscribe_cta',
    page: 'notes_from_the_road',
    kind: 'markdown',
    label: 'Empty-state call to action — button label',
    helpText:
      'Button shown in the archive section while no notes are published. Sends people to the early-access page.',
    default: 'Get Early Access',
  },

  // ─── RETIRED: the old inline subscribe capture (Brevo waitlist form) ───
  {
    key: 'notes_from_the_road.subscribe_eyebrow',
    page: 'notes_from_the_road',
    kind: 'markdown',
    label: 'Subscribe capture — eyebrow',
    helpText: 'Shown above the email capture in the empty-state archive section.',
    default: 'SUBSCRIBE',
    // Retired 2026-07-27 — the empty-state now sends people to the early-access
    // page (which owns the capture form site-wide) instead of collecting inline,
    // so this copy is no longer rendered. Kept (append-only registry) so any
    // override row does not orphan; drops out of the admin CMS editor.
    deprecated: true,
  },
  {
    key: 'notes_from_the_road.subscribe_headline',
    page: 'notes_from_the_road',
    kind: 'markdown',
    label: 'Subscribe capture — headline',
    default: 'Be first to read it.',
    // Retired 2026-07-27 — the empty-state now sends people to the early-access
    // page (which owns the capture form site-wide) instead of collecting inline,
    // so this copy is no longer rendered. Kept (append-only registry) so any
    // override row does not orphan; drops out of the admin CMS editor.
    deprecated: true,
  },
  {
    key: 'notes_from_the_road.subscribe_body',
    page: 'notes_from_the_road',
    kind: 'markdown',
    label: 'Subscribe capture — body',
    default: "Leave your email and we'll send the first note the moment it posts.",
    // Retired 2026-07-27 — the empty-state now sends people to the early-access
    // page (which owns the capture form site-wide) instead of collecting inline,
    // so this copy is no longer rendered. Kept (append-only registry) so any
    // override row does not orphan; drops out of the admin CMS editor.
    deprecated: true,
  },
  {
    key: 'notes_from_the_road.subscribe_submit',
    page: 'notes_from_the_road',
    kind: 'markdown',
    label: 'Subscribe capture — submit button label',
    default: 'Notify me',
    // Retired 2026-07-27 — the empty-state now sends people to the early-access
    // page (which owns the capture form site-wide) instead of collecting inline,
    // so this copy is no longer rendered. Kept (append-only registry) so any
    // override row does not orphan; drops out of the admin CMS editor.
    deprecated: true,
  },
  {
    key: 'notes_from_the_road.subscribe_success_eyebrow',
    page: 'notes_from_the_road',
    kind: 'markdown',
    label: 'Subscribe capture — success eyebrow',
    helpText: 'Shown after a successful email capture submission.',
    default: "YOU'RE ON THE LIST",
    // Retired 2026-07-27 — the empty-state now sends people to the early-access
    // page (which owns the capture form site-wide) instead of collecting inline,
    // so this copy is no longer rendered. Kept (append-only registry) so any
    // override row does not orphan; drops out of the admin CMS editor.
    deprecated: true,
  },
  {
    key: 'notes_from_the_road.subscribe_success_headline',
    page: 'notes_from_the_road',
    kind: 'markdown',
    label: 'Subscribe capture — success headline',
    default: 'Thanks. Watch your inbox.',
    // Retired 2026-07-27 — the empty-state now sends people to the early-access
    // page (which owns the capture form site-wide) instead of collecting inline,
    // so this copy is no longer rendered. Kept (append-only registry) so any
    // override row does not orphan; drops out of the admin CMS editor.
    deprecated: true,
  },
  {
    key: 'notes_from_the_road.subscribe_success_body',
    page: 'notes_from_the_road',
    kind: 'markdown',
    label: 'Subscribe capture — success body',
    default: "We'll email you the moment the first note posts.",
    // Retired 2026-07-27 — the empty-state now sends people to the early-access
    // page (which owns the capture form site-wide) instead of collecting inline,
    // so this copy is no longer rendered. Kept (append-only registry) so any
    // override row does not orphan; drops out of the admin CMS editor.
    deprecated: true,
  },

  // ─── Index "Where we're headed" next-steps block ──────────────────────
  {
    key: 'notes_from_the_road.next_title',
    page: 'notes_from_the_road',
    kind: 'markdown',
    label: 'Next steps — title',
    default: "Where we're headed.",
  },
  {
    key: 'notes_from_the_road.next_lede',
    page: 'notes_from_the_road',
    kind: 'markdown',
    label: 'Next steps — lede',
    default: 'The notes look back. These pages look forward.',
    // Closing-block subheading removed site-wide at Richie's request — no longer
    // rendered. Retired (not deleted) so any override doesn't orphan; drops out
    // of the admin CMS editor.
    deprecated: true,
  },
  {
    key: 'notes_from_the_road.next_tile1_title',
    page: 'notes_from_the_road',
    kind: 'markdown',
    label: 'Next steps — tile 1 title',
    helpText: 'Links to How it works.',
    default: 'How it works',
    // Superseded 2026-07-27 by the site-wide `next_steps` slots (see next_steps.ts).
    deprecated: true,
  },
  {
    key: 'notes_from_the_road.next_tile1_desc',
    page: 'notes_from_the_road',
    kind: 'markdown',
    label: 'Next steps — tile 1 description',
    default: 'The platform itself, end to end, both sides.',
    // Superseded 2026-07-27 by the site-wide `next_steps` slots (see next_steps.ts).
    deprecated: true,
  },
  {
    key: 'notes_from_the_road.next_tile2_title',
    page: 'notes_from_the_road',
    kind: 'markdown',
    label: 'Next steps — tile 2 title',
    helpText: 'Links to Founding venues.',
    default: 'Founding venues',
    // Superseded 2026-07-27 by the site-wide `next_steps` slots (see next_steps.ts).
    deprecated: true,
  },
  {
    key: 'notes_from_the_road.next_tile2_desc',
    page: 'notes_from_the_road',
    kind: 'markdown',
    label: 'Next steps — tile 2 description',
    default: 'The offer to the first 50 venues. Same price for life.',
    // Superseded 2026-07-27 by the site-wide `next_steps` slots (see next_steps.ts).
    deprecated: true,
  },
  {
    key: 'notes_from_the_road.next_tile3_title',
    page: 'notes_from_the_road',
    kind: 'markdown',
    label: 'Next steps — tile 3 title',
    helpText: 'Links to The WWAAVE Promise.',
    default: 'The WWAAVE Promise',
    // Superseded 2026-07-27 by the site-wide `next_steps` slots (see next_steps.ts).
    deprecated: true,
  },
  {
    key: 'notes_from_the_road.next_tile3_desc',
    page: 'notes_from_the_road',
    kind: 'markdown',
    label: 'Next steps — tile 3 description',
    default: 'No cuts, no paid placement, no data sales. What we will never do.',
    // Superseded 2026-07-27 by the site-wide `next_steps` slots (see next_steps.ts).
    deprecated: true,
  },

  // ─── Letter-detail 404 branch (letter not found) ──────────────────────
  {
    key: 'notes_from_the_road.notfound_hero_eyebrow',
    page: 'notes_from_the_road',
    kind: 'markdown',
    label: 'Not-found hero eyebrow',
    helpText: 'Shown when a letter URL no longer resolves.',
    default: 'Notes from the road',
  },
  {
    key: 'notes_from_the_road.notfound_hero_title',
    page: 'notes_from_the_road',
    kind: 'markdown',
    label: 'Not-found hero title',
    default: "This letter isn't here.",
  },
  {
    key: 'notes_from_the_road.notfound_hero_lede',
    page: 'notes_from_the_road',
    kind: 'markdown',
    label: 'Not-found hero lede',
    default: 'It may have been moved or unpublished. The full index of letters is one click away.',
  },
  {
    key: 'notes_from_the_road.notfound_back_link',
    page: 'notes_from_the_road',
    kind: 'markdown',
    label: 'Not-found back link',
    helpText: 'Link text back to the letter index.',
    default: '← All notes from the road',
  },

  // ─── Letter-detail "Keep reading" next-steps block ────────────────────
  {
    key: 'notes_from_the_road.keep_title',
    page: 'notes_from_the_road',
    kind: 'markdown',
    label: 'Keep reading — title',
    default: 'Keep reading.',
  },
  {
    key: 'notes_from_the_road.keep_lede',
    page: 'notes_from_the_road',
    kind: 'markdown',
    label: 'Keep reading — lede',
    default: 'Earlier notes, plus the pages they often refer back to.',
  },
  {
    key: 'notes_from_the_road.keep_tile1_title',
    page: 'notes_from_the_road',
    kind: 'markdown',
    label: 'Keep reading — tile 1 title',
    helpText: 'Links to All notes.',
    default: 'All notes',
  },
  {
    key: 'notes_from_the_road.keep_tile1_desc',
    page: 'notes_from_the_road',
    kind: 'markdown',
    label: 'Keep reading — tile 1 description',
    default: 'Every letter Richie has written, in order.',
  },
  {
    key: 'notes_from_the_road.keep_tile2_title',
    page: 'notes_from_the_road',
    kind: 'markdown',
    label: 'Keep reading — tile 2 title',
    helpText: 'Links to How it works.',
    default: 'How it works',
  },
  {
    key: 'notes_from_the_road.keep_tile2_desc',
    page: 'notes_from_the_road',
    kind: 'markdown',
    label: 'Keep reading — tile 2 description',
    default: 'The flow the letters keep referring back to.',
  },
  {
    key: 'notes_from_the_road.keep_tile3_title',
    page: 'notes_from_the_road',
    kind: 'markdown',
    label: 'Keep reading — tile 3 title',
    helpText: 'Links to Founding venues.',
    default: 'Founding venues',
  },
  {
    key: 'notes_from_the_road.keep_tile3_desc',
    page: 'notes_from_the_road',
    kind: 'markdown',
    label: 'Keep reading — tile 3 description',
    default: 'The offer to the first 50 venues, in plain numbers.',
  },
];
